import { match } from 'ts-pattern';

import { assert } from '../assert';

import { commercialTiers } from './tiers';
import type { PackagePricing, PriceVariables, TierId } from './types';

type TierIdWithPrice = Exclude<TierId, 'enterprise' | 'free'>;

const starterPrices: PackagePricing = {
  individual: { price: 0 },
  commercial: { price: 1045 },
  academic: { price: 1580 },
};

const professionalPrices: PackagePricing = {
  individual: { price: 300 },
  commercial: { price: 2900 },
  academic: {
    price: 3900,
    firstYearDiscount: 50,
  },
};

const prices: Record<TierIdWithPrice, PackagePricing> = {
  professional: professionalPrices,
  starter: starterPrices,
};

export function getPriceAndDiscount(
  tierId: TierIdWithPrice,
  variables: PriceVariables,
) {
  const tier = commercialTiers.find((tier) => tier.id === tierId);
  assert(tier);
  const { licenseType, userPlan } = variables;
  const seats = tier.seats(variables);

  const { price, firstYearDiscount = 0 } = prices[tierId][licenseType];

  let regularPrice = price;
  const hasDiscount = firstYearDiscount !== 0;

  if (seats) {
    regularPrice = match(userPlan)
      .with(10, () => regularPrice)
      .with(3, () => round10(0.6, regularPrice))
      .with(1, () => round10(0.4, regularPrice))
      .exhaustive();
  }
  const firstYearPrice = hasDiscount
    ? (regularPrice * firstYearDiscount) / 100
    : null;

  return {
    value: String(regularPrice),
    firstYearDiscount: hasDiscount ? firstYearDiscount : null,
    firstYearValue:
      firstYearPrice === null
        ? null
        : Number.isInteger(firstYearPrice)
          ? String(firstYearPrice)
          : firstYearPrice.toFixed(2),
  };
}

function round10(factor: number, value: number) {
  return Math.round((value / 10) * factor) * 10;
}
